/**
 * Phase 8 · Addendum F §33.3 — Sourcing connector status banner.
 *
 * Rendered inside the intake card and above the canvas to answer a single
 * question the Wrangle frame puts front-and-centre: "which channels am
 * I actually sourcing over right now?"
 *
 * Behaviour:
 *   - Reads `ggj_b2b_source_connectors` via `sourceConnectorsService.list()`.
 *     The row with `kind='internal'` is always considered active
 *     (it's the fallback — the org's own pool is always queryable).
 *   - Non-internal rows show as pills: `LinkedIn · active`, `Naukri · expired`, …
 *   - When a kind is missing entirely we show a muted `Connect` pill that
 *     deep-links into `/settings/integrations#sourcing-connectors`.
 *
 * The banner never blocks submit — even an org with zero external
 * connectors can source against its internal pool.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import {
  sourceConnectorsService,
  type SourceConnectorKind,
  type SourceConnectorRow,
} from '@core/supabase/services/sourceConnectorsService';

const LABELS: Record<SourceConnectorKind, string> = {
  internal: 'Internal pool',
  linkedin: 'LinkedIn',
  naukri: 'Naukri',
  github: 'GitHub',
  gitlab: 'GitLab',
  discord: 'Discord',
};

const EXTERNAL_KINDS: SourceConnectorKind[] = [
  'linkedin',
  'naukri',
  'github',
  'gitlab',
  'discord',
];

type Status = 'active' | 'inactive' | 'unconfigured';

interface PillData {
  kind: SourceConnectorKind;
  status: Status;
  reason?: string;
}

function statusOf(
  kind: SourceConnectorKind,
  rows: SourceConnectorRow[],
): PillData {
  if (kind === 'internal') return { kind, status: 'active' };
  const row = rows.find((r) => r.kind === kind);
  if (!row) return { kind, status: 'unconfigured' };
  if (row.status === 'active') return { kind, status: 'active' };
  return { kind, status: 'inactive', reason: row.status };
}

interface Props {
  /** Compact = single-line inside the intake card. Full = above canvas. */
  variant?: 'compact' | 'full';
}

export function SourcingConnectorStatus({ variant = 'compact' }: Props) {
  const { user } = useAuth();
  const orgId = user?.orgId ?? null;
  const [rows, setRows] = useState<SourceConnectorRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!orgId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    sourceConnectorsService
      .list(orgId)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const allKinds: SourceConnectorKind[] = ['internal', ...EXTERNAL_KINDS];
  const pills = allKinds.map((k) => statusOf(k, rows));
  const activeCount = pills.filter((p) => p.status === 'active').length;

  const title =
    activeCount <= 1
      ? 'Sourcing over your internal pool.'
      : `Sourcing over ${activeCount} channels.`;
  const subtitle =
    activeCount <= 1
      ? 'Connect LinkedIn, Naukri, GitHub, GitLab, or Discord in Settings → Integrations to expand the search.'
      : 'Agent will fall back to your internal pool automatically when an external channel is unreachable.';

  return (
    <div
      className="rounded-[10px] border px-3 py-2.5"
      style={{
        borderColor: 'var(--vscode-border-primary)',
        background: 'var(--vscode-bg-elevated)',
      }}
      data-ggj-sourcing-connector-status
    >
      <div className="flex items-start gap-2">
        <ShieldCheck
          className="mt-0.5 h-4 w-4 shrink-0"
          style={{ color: 'var(--ggj-accent,#10B981)' }}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p
            className="text-[12px] font-medium"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            {title}
          </p>
          {variant === 'full' ? (
            <p
              className="mt-0.5 text-[11px]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              {subtitle}
            </p>
          ) : null}
        </div>
        <Link
          to="/settings/integrations#sourcing-connectors"
          className="inline-flex shrink-0 items-center gap-1 rounded-[6px] px-2 py-0.5 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{ color: 'var(--vscode-text-secondary)' }}
        >
          Configure
          <ExternalLink className="h-3 w-3" aria-hidden />
        </Link>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Configured sourcing channels">
        {loading ? (
          <span
            className="rounded-full px-2 py-0.5 text-[10.5px]"
            style={{
              background: 'var(--vscode-bg-hover)',
              color: 'var(--vscode-text-tertiary)',
            }}
          >
            Checking channels…
          </span>
        ) : (
          pills.map((p) => <ConnectorPill key={p.kind} pill={p} />)
        )}
      </div>
    </div>
  );
}

function ConnectorPill({ pill }: { pill: PillData }) {
  const label = LABELS[pill.kind];
  const isInternal = pill.kind === 'internal';

  if (pill.status === 'active') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium"
        style={{
          background: 'var(--ggj-accent-soft,#D1FAE5)',
          color: '#065F46',
        }}
      >
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: 'var(--ggj-accent,#10B981)' }}
          aria-hidden
        />
        {label}
        {isInternal ? ' · fallback' : ''}
      </span>
    );
  }

  if (pill.status === 'inactive') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-tertiary)',
        }}
        title={pill.reason ?? undefined}
      >
        {label} · {pill.reason ?? 'inactive'}
      </span>
    );
  }

  return (
    <Link
      to="/settings/integrations#sourcing-connectors"
      className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-[10.5px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
      style={{
        borderColor: 'var(--vscode-border-primary)',
        color: 'var(--vscode-text-tertiary)',
      }}
    >
      {label} · Connect
    </Link>
  );
}
